import { logger } from 'monitor';

/**
 * Plays an audio file from the given URL.
 *
 * @param audioUrl - The URL of the audio file to play
 * @param volume - The volume level (0.0 to 1.0), defaults to 1.0
 * @returns true if audio playback was initiated, false if failed or not supported
 *
 * @example
 * playAudio('/sounds/error.mp3');
 */
export const playAudio = async (audioUrl: string, volume = 1.0): Promise<boolean> => {
  try {
    const audio = new Audio(audioUrl);
    audio.volume = Math.max(0, Math.min(1, volume));
    await audio.play();
    logger.info('Audio played successfully', { audioUrl, volume: audio.volume });
    return true;
  } catch (error) {
    let errorMessage = 'Unknown error';
    let errorName = 'Error';

    if (error instanceof DOMException) {
      logger.debug('DOMException', error);
      errorMessage = error.message;
      errorName = error.name;

      if (error.name === 'NotAllowedError') {
        logger.warn('Audio playback blocked - user interaction required', { audioUrl });
      } else if (error.name === 'NotSupportedError') {
        logger.error('Audio format not supported', { audioUrl, errorMessage });
      }
    } else if (error instanceof Error) {
      logger.debug('Error', error);
      errorMessage = error.message;
      errorName = error.name;
    }

    logger.error('Failed to play audio :' + errorName + errorMessage, {
      audioUrl,
      errorDetails: error,
      errorMessage,
      errorName,
    });
    return false;
  }
};
